import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Phone, Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { Callout, Button, type CalloutVariant } from '@/shared/ui';
import { useToast } from '@/shared/ui/Toast';
import { formatScheduledCallTime } from '@/shared/utils/format';
import { strategyApi, type CallState } from '../api/strategyApi';
import { strategyKeys } from '../queryKeys';
import styles from './VapiCallBanner.module.css';

const MAX_ATTEMPTS = 3;

interface CallStateConfig {
  message: string;
  cta: string | null;
  icon: 'phone' | 'clock' | 'alert' | 'check';
  action: 'open' | 'cancel' | 'review' | null;
}

interface VapiCallBannerProps {
  locationId: string;
  onOpen: () => void;
  onReview?: () => void;
  suppressPartial?: boolean;
}

function stateConfig(
  callState: CallState | null,
  attempts: number,
  scheduledAt: string | null,
): CallStateConfig {
  if (!callState || callState === 'cancelled') {
    return {
      message: 'Rather talk than tap? Have our AI assistant call you to fill in your strategy.',
      cta: 'Call me',
      icon: 'phone',
      action: 'open',
    };
  }
  if (callState === 'scheduling' || callState === 'dialing') {
    const time = scheduledAt ? formatScheduledCallTime(scheduledAt) : null;
    return {
      message: time ? `We'll call you at ${time}.` : "We'll be calling you shortly.",
      cta: 'Cancel call',
      icon: 'clock',
      action: 'cancel',
    };
  }
  if (callState === 'in_progress') {
    return {
      message: 'Your call is in progress.',
      cta: null,
      icon: 'phone',
      action: null,
    };
  }
  if (callState === 'processing') {
    return {
      message:
        "Thanks for the call — we're saving your answers. This usually takes under a minute.",
      cta: null,
      icon: 'clock',
      action: null,
    };
  }
  if (callState === 'no_answer' || callState === 'voicemail') {
    if (attempts >= MAX_ATTEMPTS) {
      return {
        message: `We've tried reaching you ${attempts} times without getting through. Schedule a better time?`,
        cta: 'Reschedule',
        icon: 'alert',
        action: 'open',
      };
    }
    return {
      message: `We tried calling but couldn't reach you (attempt ${attempts}). Want us to try again?`,
      cta: 'Call me again',
      icon: 'alert',
      action: 'open',
    };
  }
  if (callState === 'wrong_party') {
    return {
      message: 'We reached the wrong person — please check your number.',
      cta: 'Update & retry',
      icon: 'alert',
      action: 'open',
    };
  }
  if (callState === 'failed') {
    return {
      message: 'Something went wrong with the call. Want to try again?',
      cta: 'Try again',
      icon: 'alert',
      action: 'open',
    };
  }
  if (callState === 'not_captured' || callState === 'ended') {
    return {
      message: "The call ended but we couldn't capture your answers. Try again?",
      cta: 'Try again',
      icon: 'alert',
      action: 'open',
    };
  }
  if (callState === 'captured_partial') {
    return {
      message: 'We captured most answers — a few gaps remain.',
      cta: 'Fill in gaps →',
      icon: 'check',
      action: 'review',
    };
  }
  // captured_complete, confirmed — hide banner
  return { message: '', cta: null, icon: 'phone', action: null };
}

const ICON_VARIANT: Record<CallStateConfig['icon'], CalloutVariant> = {
  alert: 'warning',
  check: 'accent',
  phone: 'neutral',
  clock: 'info',
};

const ICON_COMPONENT = {
  phone: Phone,
  clock: Clock,
  alert: AlertCircle,
  check: CheckCircle,
} as const;

export function VapiCallBanner({
  locationId,
  onOpen,
  onReview,
  suppressPartial,
}: VapiCallBannerProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: strategyKeys.vapiSession(locationId),
    queryFn: () => strategyApi.getVapiSession(locationId),
    enabled: !!locationId,
    refetchInterval: (query) => {
      const session = query.state.data?.data;
      const state = session?.callState;
      if (state === 'dialing' || state === 'in_progress' || state === 'processing') return 5000;
      // An immediate call (or a scheduled one whose time has come) sits in
      // `scheduling` until Vapi reports it ringing — keep polling so the
      // banner moves on without a reload.
      if (
        state === 'scheduling' &&
        (!session?.scheduledAt || new Date(session.scheduledAt).getTime() <= Date.now())
      ) {
        return 5000;
      }
      return false;
    },
  });

  const session = data?.data ?? null;
  const callState = session?.callState ?? null;
  const attempts = session?.attempts ?? 0;
  const scheduledAt = session?.scheduledAt ?? null;

  const { showToast } = useToast();
  const cancelMutation = useMutation({
    mutationFn: () => strategyApi.cancelVapiSession(locationId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: strategyKeys.vapiSession(locationId) });
    },
    onError: (err: unknown) => {
      showToast((err as Error).message || "Couldn't cancel the call. Please try again.", 'error');
    },
  });

  // When a call captures answers, refresh the questionnaire so they appear.
  const lastState = useRef(callState);
  useEffect(() => {
    if (lastState.current === callState) return;
    lastState.current = callState;
    if (callState === 'captured_complete' || callState === 'captured_partial') {
      void queryClient.invalidateQueries({ queryKey: strategyKeys.responses(locationId) });
    }
  }, [callState, locationId, queryClient]);

  const config = stateConfig(callState, attempts, scheduledAt);

  // Hide for terminal success states
  if (callState === 'captured_complete' || callState === 'confirmed') return null;
  if (suppressPartial && callState === 'captured_partial') return null;
  if (!config.message) return null;

  function handleCta() {
    if (config.action === 'open') onOpen();
    else if (config.action === 'cancel') cancelMutation.mutate();
    else if (config.action === 'review') {
      if (onReview) onReview();
      else navigate('/strategy/builder/profile');
    }
  }

  return (
    <Callout
      variant={ICON_VARIANT[config.icon]}
      icon={ICON_COMPONENT[config.icon]}
      actions={
        config.cta ? (
          <Button
            size="sm"
            variant="secondary"
            onClick={handleCta}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? 'Cancelling…' : config.cta}
          </Button>
        ) : undefined
      }
      className={styles.banner}
    >
      {config.message}
    </Callout>
  );
}
